import React from "react";
import { AlertTriangle, XCircle } from "lucide-react";
import { Card, CardBody, Badge, Row, Col } from "reactstrap";
import { getAvailabilityStatusClass } from "../../../../../utility/inventoryUtils";
import "./index.css";
import { ProductOverviewItem } from "../../../../../redux/services/allocationService";

interface OverviewTabProps {
  products: ProductOverviewItem[];
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ products }) => {
  const [selectedProductId, setSelectedProductId] = React.useState<
    string | null
  >(null);

  // Handle empty state
  if (!products || products.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        No product data available for today
      </div>
    );
  }

  return (
    <div className="inventory-overview-tab-content">
      {products.map((product) => {
        // Get values from multiple possible locations with defaults
        const produced = product.quantities?.produced || product.quantities.produced || 0;
        const allocated = product.quantities?.allocated || product.quantities.allocated || 0;
        const reserved = product.quantities?.reserved || 0;
        const cancelled = product.quantities?.cancelled || 0;

        // NEW LOGIC: Available calculation
        // If allocated > 0: Available = Produced - Allocated + Cancelled
        // If allocated = 0: Available = Produced - Reserved + Cancelled
        let available;
        if (allocated > 0) {
          available = produced - allocated + cancelled;
        } else {
          available = produced - reserved + cancelled;
        }

        return (
          <Card
            key={product.id}
            className={`inventory-product-overview-card mb-3 ${
              selectedProductId === product.id
                ? "inventory-product-selected"
                : ""
            }`}
            onClick={() =>
              setSelectedProductId(
                selectedProductId === product.id ? null : product.id
              )
            }
          >
            <CardBody className="inventory-product-overview-card-body">
              <Row className="align-items-center">
                <Col md={2} className="inventory-product-info-column">
                  <div className="inventory-product-info-container d-flex align-items-center">
                    <div>
                      <div className="inventory-product-name fw-bold">
                        {product.displayName || 'Unnamed Product'}
                      </div>
                      <div className="inventory-product-sku small text-muted">
                        {product.productId || product.productId || 'N/A'}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md className="inventory-metric-column text-center">
                  <div className="inventory-metric-label small text-muted">
                    Produced
                  </div>
                  <div className="inventory-metric-value h5 fw-bold">
                    {produced}
                  </div>
                </Col>
                <Col md className="inventory-metric-column text-center">
                  <div className="inventory-metric-label small text-muted">
                    Allocated
                  </div>
                  <div className="inventory-metric-value h5 fw-bold">
                    {allocated}
                  </div>
                </Col>
                <Col md className="inventory-metric-column text-center">
                  <div className="inventory-metric-label small text-muted">
                    Orders (Reserved)
                  </div>
                  <div className={`inventory-metric-value h5 fw-bold`}>
                    {reserved}
                  </div>
                </Col>
                <Col md className="inventory-metric-column text-center">
                  <div className="inventory-metric-label small text-muted">
                    Cancelled
                  </div>
                  <div className="inventory-metric-value h5 fw-bold text-secondary d-flex align-items-center justify-content-center">
                    <XCircle size={16} className="me-1" />
                    {cancelled}
                  </div>
                </Col>
                <Col md className="inventory-metric-column text-center">
                  <div className="inventory-metric-label small text-muted">
                    Available
                  </div>
                  <div
                    className={`inventory-metric-value h5 fw-bold ${getAvailabilityStatusClass(
                      available
                    )}`}
                  >
                    {available}
                  </div>
                </Col>
                <Col md={2} className="inventory-status-column text-end">
                  {available < 0 ? (
                    <Badge color="danger" className="inventory-status-badge">
                      <AlertTriangle className="inventory-status-icon" />
                      Over-allocated
                    </Badge>
                  ) : available < 5 ? (
                    <Badge color="warning" className="inventory-status-badge">
                      <AlertTriangle className="inventory-status-icon" />
                      Low Stock
                    </Badge>
                  ) : (
                    <Badge color="success" className="inventory-status-badge">
                      Healthy
                    </Badge>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};